import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Camera, ShieldCheck, ShieldAlert, KeyRound, Bell, LogOut, ChevronRight, Building2, Inbox, Landmark, Heart, CalendarCheck2, MessageCircle, BadgeCheck, Gift, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PasswordInput } from "@/pages/Register";
import BankDetailsSection from "@/components/locanest/BankDetailsSection";
import ChangeEmailDialog from "@/components/locanest/ChangeEmailDialog";
import VerifiedBadge from "@/components/locanest/VerifiedBadge";
import TierBadge from "@/components/locanest/TierBadge";
import LocationSearch from "@/components/locanest/LocationSearch";

const ROLE_LABEL = { tenant: "Tenant", owner: "Owner / Broker", admin: "Platform Admin" };

export default function ProfilePage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    const section = params.get("section");
    if (!section) return;
    const t = setTimeout(() => document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, [params]);

  if (!user) return null;
  const isOwner = user.role === "owner";
  const pc = user.profile_completion;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12" data-testid="profile-page">
      <ProfileHeader user={user} onChanged={refresh} />

      {pc && pc.percent < 100 && (
        <div className="mt-6 rounded-lg bg-[#F0EBE1] border border-[#2B3A67]/20 p-4" data-testid="profile-completion-summary">
          <div className="flex items-center justify-between text-sm">
            <span>Profile <strong>{pc.percent}%</strong> complete</span>
            <span className="text-xs text-[#5C6B7A]">{pc.filled}/{pc.total} sections</span>
          </div>
          <div className="mt-2 h-2 bg-[#FDFBF7] rounded-full overflow-hidden"><div className="h-full bg-[#C85A32]" style={{ width: `${pc.percent}%` }} /></div>
          {pc.missing?.length > 0 && <div className="mt-2 text-xs text-[#5C6B7A]">Still to do: <span className="text-[#1A1F2C]">{pc.missing.join(" · ")}</span></div>}
        </div>
      )}

      <QuickLinks user={user} />

      <SectionTitle id="section-details" icon={<Building2 size={16} />} title="Personal details" sub="Your name and phone are shown to people you deal with." />
      <DetailsForm user={user} onSaved={refresh} />

      {isOwner && (
        <>
          <SectionTitle id="section-verification" icon={<BadgeCheck size={16} />} title="Owner verification" sub="Aadhaar + PAN (and business proof for commercial listings)." />
          <VerificationCard user={user} />
          <SectionTitle id="section-bank" icon={<Landmark size={16} />} title="Bank details for payouts" sub="LocaNest transfers tenant payments to this account within 3–4 days." />
          <BankDetailsSection user={user} onSaved={refresh} />
        </>
      )}

      <SectionTitle id="section-password" icon={<KeyRound size={16} />} title="Change password" />
      <ChangePassword />

      <SectionTitle id="section-notifications" icon={<Bell size={16} />} title="Notification preferences" sub="Emails go to your registered address." />
      <NotificationPrefs user={user} onSaved={refresh} />

      <div className="mt-10 flex flex-wrap gap-3 items-center justify-between border-t border-[#1A1F2C]/10 pt-6">
        <Link to="/support" className="text-sm text-[#2B3A67] inline-flex items-center gap-1 hover:text-[#C85A32]" data-testid="profile-support-link"><LifeBuoy size={14} /> Help &amp; Support</Link>
        <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} className="rounded-md" data-testid="profile-logout"><LogOut size={14} className="mr-1" /> Log out</Button>
      </div>
    </div>
  );
}

function SectionTitle({ id, icon, title, sub }) {
  return (
    <div id={id} className="mt-10 mb-3 scroll-mt-24">
      <div className="flex items-center gap-2 text-[#2B3A67]">{icon}<h2 className="font-display text-xl">{title}</h2></div>
      {sub && <p className="text-xs text-[#5C6B7A] mt-1">{sub}</p>}
    </div>
  );
}

function ProfileHeader({ user, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) { toast.error("Photo must be under 1 MB"); return; }
    const r = new FileReader();
    r.onload = async () => {
      setBusy(true);
      try { await api.put("/users/me", { avatar: r.result }); toast.success("Profile photo updated"); onChanged(); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      finally { setBusy(false); }
    };
    r.readAsDataURL(f);
  };
  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-xl bg-[#2B3A67] text-[#FDFBF7] p-6 flex items-center gap-5" data-testid="profile-header">
      <button type="button" onClick={() => fileRef.current?.click()} className="relative w-20 h-20 rounded-full overflow-hidden bg-[#C85A32] grid place-items-center text-2xl font-display shrink-0" data-testid="avatar-upload-btn" disabled={busy}>
        {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" data-testid="avatar-img" /> : initials}
        <span className="absolute bottom-0 inset-x-0 bg-black/45 text-[10px] py-0.5 flex items-center justify-center gap-1"><Camera size={10} /> {busy ? "…" : "Edit"}</span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-file-input" />
      <div className="min-w-0">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#FDFBF7]/15 text-[11px] uppercase tracking-[0.2em]" data-testid="profile-role-badge">
          {ROLE_LABEL[user.role]}
        </div>
        <h1 className="font-display text-2xl md:text-3xl mt-2 truncate" data-testid="profile-name">{user.name}</h1>
        <div className="text-sm text-[#FDFBF7]/80 truncate" data-testid="profile-email">{user.email}</div>
        <div className="text-sm text-[#FDFBF7]/80" data-testid="profile-phone">{user.phone ? `+91 ${user.phone}` : "No phone added"}</div>
        {user.role === "owner" && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {user.is_verified ? <VerifiedBadge /> : <span className="text-[11px] px-2 py-0.5 rounded bg-[#FDFBF7]/15" data-testid="profile-unverified">Not verified yet</span>}
            <TierBadge tier={user.vendor_tier || "new"} />
          </div>
        )}
      </div>
    </div>
  );
}

function QuickLinks({ user }) {
  const links = user.role === "owner"
    ? [
        { to: "/owner?tab=listings", label: "My Listings", Icon: Building2, t: "ql-listings" },
        { to: "/owner?tab=inquiries", label: "Inquiries", Icon: Inbox, t: "ql-inquiries" },
        { to: "/owner?tab=chats", label: "Chats", Icon: MessageCircle, t: "ql-chats" },
        { to: "/profile?section=bank", label: "Bank details", Icon: Landmark, t: "ql-bank" },
      ]
    : user.role === "tenant"
    ? [
        { to: "/bookings", label: "My Bookings", Icon: CalendarCheck2, t: "ql-bookings" },
        { to: "/favorites", label: "Favorites", Icon: Heart, t: "ql-favorites" },
        { to: "/chats", label: "Chats", Icon: MessageCircle, t: "ql-chats" },
        { to: "/bookings", label: "Refer & earn", Icon: Gift, t: "ql-referral" },
      ]
    : [{ to: "/admin", label: "Admin dashboard", Icon: ShieldCheck, t: "ql-admin" }];
  return (
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="profile-quick-links">
      {links.map(({ to, label, Icon, t }) => (
        <Link key={label} to={to} data-testid={t} className="rounded-lg bg-[#F0EBE1] border border-[#1A1F2C]/10 p-3 flex items-center justify-between hover:border-[#C85A32]/40 transition-colors">
          <span className="flex items-center gap-2 text-sm"><Icon size={15} className="text-[#C85A32]" /> {label}</span>
          <ChevronRight size={14} className="text-[#5C6B7A]" />
        </Link>
      ))}
    </div>
  );
}

function DetailsForm({ user, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", business_name: user.business_name || "", preferred_city: user.preferred_city || "", preferred_locality: user.preferred_locality || "" });
  const [locLabel, setLocLabel] = useState(user.preferred_city ? `${user.preferred_locality ? `${user.preferred_locality}, ` : ""}${user.preferred_city}` : "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({ name: user.name || "", phone: user.phone || "", business_name: user.business_name || "", preferred_city: user.preferred_city || "", preferred_locality: user.preferred_locality || "" });
    setLocLabel(user.preferred_city ? `${user.preferred_locality ? `${user.preferred_locality}, ` : ""}${user.preferred_city}` : "");
  }, [user]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: form.name, phone: form.phone };
      if (user.role === "owner") body.business_name = form.business_name;
      if (user.role === "tenant") { body.preferred_city = form.preferred_city; body.preferred_locality = form.preferred_locality; }
      await api.put("/users/me", body);
      toast.success("Profile updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="rounded-lg bg-[#F0EBE1] border border-[#1A1F2C]/10 p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="details-form">
      <div><Label>Full name</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="details-name" /></div>
      <div><Label>Mobile number</Label><Input inputMode="numeric" placeholder="10-digit mobile" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="details-phone" /></div>
      {user.role === "tenant" && (
        <div className="md:col-span-2">
          <Label>Where are you looking for a rental?</Label>
          <LocationSearch
            compact
            showGps={false}
            value={locLabel}
            onChange={(t) => { setLocLabel(t); if (!t) setForm((f) => ({ ...f, preferred_city: "", preferred_locality: "" })); }}
            onPick={(it) => { setLocLabel(it.label); setForm((f) => ({ ...f, preferred_city: it.city || "", preferred_locality: it.locality || "" })); }}
            placeholder="City or area — powers “Rentals near you” on the homepage"
            testId="details-location"
          />
        </div>
      )}
      {user.role === "owner" && (
        <div className="md:col-span-2"><Label>Business / agency name</Label><Input placeholder="e.g. Sharma Realty" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="details-business" /></div>
      )}
      <div className="md:col-span-2">
        <Label>Email (login ID)</Label>
        <div className="flex gap-2 items-center">
          <Input value={user.email} disabled data-testid="details-email" />
          <ChangeEmailDialog />
        </div>
        <div className="text-[11px] text-[#5C6B7A] mt-1">Changing it sends a verification code to the new address; once confirmed, the new email becomes your login ID.</div>
      </div>
      <div className="md:col-span-2 flex justify-end"><Button type="submit" disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="details-save">{busy ? "Saving…" : "Save changes"}</Button></div>
    </form>
  );
}

function VerificationCard({ user }) {
  const s = user.verification_status || "none";
  const tone = s === "approved" ? "border-[#2B3A67]/25 bg-[#D4DEC6]/25" : s === "pending" ? "border-[#C85A32]/30 bg-[#F0EBE1]" : s === "rejected" ? "border-red-300 bg-red-50" : "border-[#1A1F2C]/10 bg-[#F0EBE1]";
  const label = { approved: "Verified ✓ — badge is live on your listings", pending: "Submitted — awaiting admin review (usually within 24h)", rejected: "Rejected — please resubmit with clearer documents", none: "Not started — verified owners get 3× more inquiries" }[s];
  return (
    <div className={`rounded-lg border p-5 flex items-center gap-3 flex-wrap ${tone}`} data-testid="verification-card">
      {s === "approved" ? <ShieldCheck className="text-[#2B3A67]" /> : <ShieldAlert className="text-[#C85A32]" />}
      <div className="flex-1 min-w-[200px] text-sm" data-testid="verification-status-text">{label}</div>
      <Button asChild variant={s === "approved" ? "outline" : "default"} className={`rounded-md ${s === "approved" ? "" : "bg-[#C85A32] hover:bg-[#a8482a] text-white"}`} data-testid="verification-cta">
        <Link to="/owner/verify">{s === "none" ? "Start verification" : s === "rejected" ? "Resubmit documents" : "View submission"}</Link>
      </Button>
    </div>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      if (data.token) localStorage.setItem("ln_token", data.token);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg bg-[#F0EBE1] border border-[#1A1F2C]/10 p-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="change-password-form">
      <div><Label>Current password</Label><PasswordInput required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} testId="pw-current" /></div>
      <div><Label>New password</Label><PasswordInput required minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} testId="pw-new" /></div>
      <div><Label>Confirm new password</Label><PasswordInput required minLength={6} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testId="pw-confirm" /></div>
      <div className="md:col-span-3 flex justify-end"><Button type="submit" disabled={busy} variant="outline" className="rounded-md" data-testid="pw-save">{busy ? "Updating…" : "Update password"}</Button></div>
    </form>
  );
}

const PREFS = [
  ["email_inquiries", "New inquiry alerts", "When a tenant sends an inquiry or reserves a listing"],
  ["email_bookings", "Booking updates", "Payments, agreements and status changes"],
  ["email_chat", "Chat messages", "When someone messages you in-app"],
  ["email_marketing", "Tips & offers", "Occasional product news from LocaNest"],
];

function NotificationPrefs({ user, onSaved }) {
  const prefs = user.notification_prefs || {};
  const toggle = async (key, val) => {
    try {
      await api.put("/users/me", { notification_prefs: { ...prefs, [key]: val } });
      toast.success("Preference saved");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg bg-[#F0EBE1] border border-[#1A1F2C]/10 divide-y divide-[#1A1F2C]/10" data-testid="notification-prefs">
      {PREFS.map(([k, title, sub]) => (
        <div key={k} className="flex items-center justify-between gap-4 p-4">
          <div><div className="text-sm font-medium">{title}</div><div className="text-xs text-[#5C6B7A]">{sub}</div></div>
          <Switch checked={prefs[k] !== false} onCheckedChange={(v) => toggle(k, v)} data-testid={`pref-${k}`} />
        </div>
      ))}
    </div>
  );
}
